import { useRef } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { FeedTileResolver } from '../../components/Feed/FeedTileResolver'
import { LoadingIndicator } from '../../components/Loading'
import { ADS_FEED_TYPE, REGULAR_POST_TYPE } from '../../constants/FeedConstants'

// Styled components for the grid layout
const GridContainer = styled.div`
  display: grid;
  box-sizing: border-box;
  grid-template-columns: ${
    props =>
      !props.isAuthenticated
        ? 'repeat(2, minmax(0, 1fr))' // Two columns for non-authenticated users
        : 'repeat(auto-fit, minmax(0, 1fr))' // Auto-fit for authenticated users
  };
  gap: 20px;
  width: 100%;
  max-width: 100dvw;

  @media (min-width: 1920px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    margin: 0 auto;
  }

  @media (min-width: 1200px) and (max-width: 1919px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (min-width: 768px) and (max-width: 1199px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 479px) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
    gap: 10px;
    padding: 0 10px;
  }
  ${props =>
    !props.isAuthenticated &&
    `@media (max-width: 479px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }`}

  @media (min-width: 800px) {
    padding: 0 20px;
  }
`

const PostContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 0;
  flex-shrink: 1;
`

const PostTitle = styled.a`
  font-weight: bold;
  font-size: 14px;
  line-height: 1.4;
  padding: 12px 16px 16px;
  margin: 0;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 479px) {
    font-size: 10px;
    padding: 6px 8px;
  }
`

const PostTile = styled.div`
  height: auto;
  width: auto;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 1rem;
  margin-top: 1rem;
`

const LoadMoreButton = styled.button`
  background: transparent;
  border: 2px solid #ff5e6d;
  border-radius: 25px;
  color: #ff5e6d;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;

  &:hover {
    background: #ff5e6d;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 94, 109, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 479px) {
    font-size: 14px;
    padding: 12px 24px;
    min-width: 120px;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #666;
  grid-column: 1 / -1;

  h3 {
    margin: 0 0 1rem;
    font-size: 1.5rem;
  }

  p {
    margin: 0;
    font-size: 1rem;
  }
`

export const ExploreInspirationsContainer = ({
  userInspirations = [],
  onFetchMore,
  loading = 'idle',
  end = false,
  header,
  footer,
  paddingStart = 100,
  paddingBottom = 115,
  shouldShowLoginProposal = false,
  disableAds = false,
  location = 'explore'
}) => {
  const isAuthenticated = useSelector(state => state?.auth?.isAuthenticated)
  const { likes: likesObj } = useSelector(state => state.like)
  const { savedIds } = useSelector(state => state.saveInspiration)

  const ob = useRef()

  // Calculate items per row for lazy loading threshold
  const getItemsPerRow = () => {
    if (typeof window === 'undefined') return 5
    const width = window.innerWidth
    if (width >= 1920) return 5
    if (width >= 1200) return 4
    if (width >= 768) return 2
    return 1
  }

  const itemsPerRow = getItemsPerRow()
  const lazyLoadThreshold = itemsPerRow * 4 // After 4 rows

  // Convert userInspirations to the format expected by FeedTileResolver
  const prepareTileData = (design, index) => {
    return {
      key: design.objectId || index,
      objectId: design.objectId,
      id: design.objectId,
      type: design.type || 'design',
      tileType: REGULAR_POST_TYPE,
      image: design.image,
      designThumbnail: design.designThumbnail,
      urlSlug: design.urlSlug,
      altText: design.altText,
      aiTitle: design.aiTitle,
      user: design.user,
      numLikes: design.numLikes,
      createdAt: design.createdAt,
      sourcePost: design,
      likes: design.numLikes,
      // Add other properties that FeedTileResolver expects
      ...design
    }
  }

  // Function to insert ads at fixed positions for stable prerendering
  // First ad at index 8, second at index 20, then every 16 items
  const insertAdsIntoFeed = inspirations => {
    if (!inspirations || inspirations.length === 0) return []
    if (isAuthenticated || disableAds) return inspirations

    const result = []
    const adPositions = new Set()

    // Calculate all ad positions: 8, 20, 36, 52, 68, etc.
    let position = 8
    while (position < inspirations.length) {
      adPositions.add(position)
      if (position === 8) {
        position = 20 // Second ad at position 20
      } else {
        position += 16 // Then every 16 items
      }
    }

    // Ad titles for each image index
    const adTitles = {
      1: 'Create your dream home with our app',
      2: 'Instant room makeovers with AI design',
      3: 'Redesign blank rooms from scratch fast',
      4: 'Play interior design games for prizes'
    }

    let adCounter = 0
    inspirations.forEach((inspiration, index) => {
      result.push(inspiration)

      // Insert ad tile at fixed positions
      if (adPositions.has(index + 1)) {
        adCounter++
        const imageIndex = ((adCounter - 1) % 4) + 1 // Cycle 1-2-3-4-1-2-3-4...
        result.push({
          key: `ad-${index}`,
          objectId: `ad-${index}`,
          tileType: ADS_FEED_TYPE,
          isAdTile: true,
          imageIndex: imageIndex,
          aiTitle: adTitles[imageIndex]
        })
      }
    })

    return result
  }

  // No content available
  if (!userInspirations || userInspirations.length === 0) {
    return (
      <GridContainer isAuthenticated={isAuthenticated} paddingStart={paddingStart} paddingBottom={paddingBottom}>
        {header}
        <EmptyState>
          <h3>No designs found</h3>
          <p>Try adjusting your filters or check back later for new content.</p>
        </EmptyState>
      </GridContainer>
    )
  }

  // Limit content for non-authenticated users with login proposal
  const baseInspirations = userInspirations

  // Insert ads into the feed
  const inspirationsWithAds = insertAdsIntoFeed(baseInspirations)

  // Ensure array length is a multiple of 5
  const targetLength = Math.floor(inspirationsWithAds.length / 5) * 5
  const finalInspirations = inspirationsWithAds.slice(0, targetLength)

  return (
    <>
      {header}
      <GridContainer isAuthenticated={isAuthenticated} paddingStart={paddingStart} paddingBottom={paddingBottom}>
        {finalInspirations.map((item, index) => {
          const isAdTile = item.isAdTile
          const tileData = isAdTile ? item : prepareTileData(item, index)

          return (
            <PostContainer key={item.key || item.objectId || index}>
              <PostTile>
                <FeedTileResolver priority={index <= lazyLoadThreshold} tile={tileData} likesObj={likesObj} savedList={savedIds} lazyLoad={index >= lazyLoadThreshold} location={location} />
              </PostTile>
              {item.aiTitle && <PostTitle href={`/design/${item.urlSlug}`}>{item.aiTitle}</PostTitle>}
            </PostContainer>
          )
        })}
      </GridContainer>

      {/* Load more button for authenticated users */}
      {isAuthenticated && !end && loading !== 'pending' && onFetchMore && (
        <LoadMoreContainer>
          <LoadMoreButton onClick={onFetchMore}>Load More</LoadMoreButton>
        </LoadMoreContainer>
      )}

      {/* Loading indicator */}
      {loading === 'pending' && (
        <LoadingContainer>
          <LoadingIndicator loading={true} />
        </LoadingContainer>
      )}

      {footer}
    </>
  )
}
